import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Row, Col, Typography, Table, Tag, Progress, Card, Statistic } from 'antd';
import {
  CloudServerOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  IdcardOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import { StatCard, DataCard } from '../components/common';
import { useTenant } from '../context/TenantContext';
import { useCIPPTenants, useCIPPUsers, useCIPPLicenses, useCIPPSecureScore } from '../hooks';
import M365NotManaged from '../components/common/M365NotManaged';
import { cippTenantIdOf, isM365Managed } from '../utils/customerKey';

const { Title, Text } = Typography;

/**
 * Helper function to parse MCP response content
 * @param {object} response - Raw MCP response
 * @returns {any} Parsed data
 */
const parseMCPResponse = (response) => {
  if (!response) return null;

  // Handle MCP content format: { content: [{ type: 'text', text: '...' }] }
  if (response.content && Array.isArray(response.content)) {
    const textContent = response.content.find(c => c.type === 'text');
    if (textContent?.text) {
      try {
        return JSON.parse(textContent.text);
      } catch (e) {
        console.error('[M365Page] Failed to parse MCP response:', e);
        return null;
      }
    }
  }

  return response;
};

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

// Table chrome comes from the root ConfigProvider (theme spec section 2.4): no page-local .ant-* overrides.
const StyledTable = styled(Table)``;

const ScoreCard = styled(Card)`
  text-align: center;
`;

const M365Page = () => {
  const { selectedTenant } = useTenant();
  // null for a customer without a CIPP id, so the per-tenant queries stay off
  const tenantId = cippTenantIdOf(selectedTenant);
  const notManaged = !!selectedTenant && !isM365Managed(selectedTenant);

  // Fetch data from CIPP-MCP via Gateway; the all-tenants overview only for "All Customers"
  const { data: tenantsRaw, isLoading: tenantsLoading } = useCIPPTenants({
    enabled: !selectedTenant,
  });
  const { data: usersRaw, isLoading: usersLoading } = useCIPPUsers(tenantId);
  const { data: licensesRaw, isLoading: licensesLoading } = useCIPPLicenses(tenantId);
  const { data: secureScoreRaw, isLoading: scoreLoading } = useCIPPSecureScore(tenantId);

  // Parse MCP responses
  const tenantsParsed = useMemo(() => parseMCPResponse(tenantsRaw), [tenantsRaw]);
  const usersParsed = useMemo(() => parseMCPResponse(usersRaw), [usersRaw]);
  const licensesParsed = useMemo(() => parseMCPResponse(licensesRaw), [licensesRaw]);
  const secureScoreParsed = useMemo(() => parseMCPResponse(secureScoreRaw), [secureScoreRaw]);

  // Extract arrays from parsed responses
  const tenants = useMemo(() => {
    const data = tenantsParsed?.tenants || tenantsParsed;
    return Array.isArray(data) ? data : [];
  }, [tenantsParsed]);

  const users = useMemo(() => {
    const data = usersParsed?.users || usersParsed;
    return Array.isArray(data) ? data : [];
  }, [usersParsed]);

  const licenses = useMemo(() => {
    const data = licensesParsed?.licenses || licensesParsed;
    return Array.isArray(data) ? data : [];
  }, [licensesParsed]);

  // Calculate statistics
  const tenantCount = tenants.length;
  const userCount = users.length;
  const licensedUsers = users.filter(u => u.assignedLicenses?.length > 0).length;

  // Extract secure score (get_secure_score returns { success, score: { percentage } })
  const scorePercent = (secureScoreParsed?.success && secureScoreParsed?.score?.percentage !== undefined)
    ? secureScoreParsed.score.percentage
    : null;

  // User table columns
  const userColumns = [
    {
      title: 'Display Name',
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: (a, b) => (a.displayName || '').localeCompare(b.displayName || ''),
    },
    {
      title: 'Email',
      dataIndex: 'mail',
      key: 'mail',
      ellipsis: true,
    },
    {
      title: 'Account Status',
      dataIndex: 'accountEnabled',
      key: 'accountEnabled',
      render: (enabled) => (
        <Tag color={enabled ? 'green' : 'red'} icon={enabled ? <CheckCircleOutlined /> : <WarningOutlined />}>
          {enabled ? 'Active' : 'Disabled'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Disabled', value: false },
      ],
      onFilter: (value, record) => record.accountEnabled === value,
    },
    {
      title: 'Licenses',
      dataIndex: 'assignedLicenses',
      key: 'licenses',
      render: (licenses) => licenses?.length || 0,
      sorter: (a, b) => (a.assignedLicenses?.length || 0) - (b.assignedLicenses?.length || 0),
    },
  ];

  // License table columns
  const licenseColumns = [
    {
      title: 'License Name',
      dataIndex: 'skuPartNumber',
      key: 'skuPartNumber',
    },
    {
      title: 'Total',
      dataIndex: 'prepaidUnits',
      key: 'total',
      render: (units) => units || 0,
    },
    {
      title: 'Consumed',
      dataIndex: 'consumedUnits',
      key: 'consumed',
    },
    {
      title: 'Available',
      key: 'available',
      render: (_, record) => {
        const total = record.prepaidUnits || 0;
        const consumed = record.consumedUnits || 0;
        return total - consumed;
      },
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (_, record) => {
        const total = record.prepaidUnits || 0;
        const consumed = record.consumedUnits || 0;
        const percent = total > 0 ? Math.round((consumed / total) * 100) : 0;
        return (
          <Progress
            percent={percent}
            size="small"
            strokeColor={percent >= 90 ? 'var(--odin-error)' : percent >= 70 ? 'var(--odin-warning)' : 'var(--odin-success)'}
          />
        );
      },
    },
  ];

  return (
    <DashboardLayout>
      <PageHeader>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          <CloudServerOutlined style={{ marginRight: 12, color: 'var(--odin-accent)' }} />
          Microsoft 365
        </Title>
        <Text style={{ color: 'var(--odin-text-secondary)' }}>
          {selectedTenant ? `${selectedTenant.displayName} - Tenant Management` : 'All Tenants Overview'}
        </Text>
      </PageHeader>

      {notManaged ? (
        <DataCard title="Microsoft 365" icon={CloudServerOutlined}>
          <M365NotManaged />
        </DataCard>
      ) : (
      <>
      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {/* Only show Tenants count when no customer is selected */}
        {!tenantId && (
          <Col xs={24} sm={12} lg={6}>
            <StatCard
              icon={CloudServerOutlined}
              value={tenantCount}
              label="Tenants"
              loading={tenantsLoading}
            />
          </Col>
        )}
        <Col xs={24} sm={12} lg={tenantId ? 8 : 6}>
          <StatCard
            icon={UserOutlined}
            value={userCount}
            label="Total Users"
            loading={usersLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={tenantId ? 8 : 6}>
          <StatCard
            icon={IdcardOutlined}
            value={licensedUsers}
            label="Licensed Users"
            color="var(--odin-success)"
            loading={usersLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={tenantId ? 8 : 6}>
          <ScoreCard>
            <Statistic
              title="Secure Score"
              value={scorePercent !== null ? scorePercent : '--'}
              suffix={scorePercent !== null ? '%' : ''}
              prefix={<SafetyCertificateOutlined style={{ color: scorePercent >= 70 ? 'var(--odin-success)' : scorePercent >= 50 ? 'var(--odin-warning)' : 'var(--odin-error)' }} />}
              loading={scoreLoading}
            />
          </ScoreCard>
        </Col>
      </Row>

      {/* Users Table */}
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <DataCard
            title="Users"
            icon={UserOutlined}
            loading={usersLoading}
            empty={users.length === 0}
            emptyText={selectedTenant ? 'No users found' : 'Select a tenant to view users'}
          >
            <StyledTable
              dataSource={users}
              columns={userColumns}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true }}
              size="small"
            />
          </DataCard>
        </Col>
      </Row>

      {/* Licenses Table */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <DataCard
            title="License Subscriptions"
            icon={IdcardOutlined}
            loading={licensesLoading}
            empty={licenses.length === 0}
            emptyText={selectedTenant ? 'No licenses found' : 'Select a tenant to view licenses'}
          >
            <StyledTable
              dataSource={licenses}
              columns={licenseColumns}
              rowKey="skuId"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </DataCard>
        </Col>
      </Row>
      </>
      )}
    </DashboardLayout>
  );
};

export default M365Page;
